import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { FormProvider, Resolver, useForm, useFormContext } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Users,
  CheckSquare,
  Calendar,
  Clock,
  BarChart3,
  User,
  Mail,
  Globe,
  Building2,
  ChevronDown,
  Loader2,
} from "lucide-react";
import registrationImage from "../assets/registration.png";

import { useToaster } from "contexts/toasterContext";
import { isValidEmail } from "util/validation";
import { isValidWebsite, preventSpaceInput, removeSpacesFromInput } from "util/preventSpecialCharInput";
import { useNavigate } from "react-router-dom";
import appConfig from "../../src/services/apiConfig"


// Add these libphonenumber-js imports:
import { parsePhoneNumberWithError, ParseError, CountryCode } from "libphonenumber-js";
import { COUNTRY_CODE_OPTIONS } from "util/phoneCountry";
import { allowOnlyAlphabets } from "util/Util";

/* ------------------------------------------------------------------ */
/*  Design tokens (kept in one place so styles are not duplicated)     */
/* ------------------------------------------------------------------ */
const COLORS = {
  primary: "#2F63D9",
  primaryHover: "#2551B8",
  primaryActive: "#1F47A0",
  heading: "#172B4D",
  secondary: "#6B7280",
  border: "#D1D5DB",
  borderHover: "#9CA3AF",
  error: "#DC2626",
  iconBg: "#EEF4FF",
  placeholder: "#9CA3AF",
};

const countryCodeOptions = COUNTRY_CODE_OPTIONS;

// Shared field shell — 44px tall, 8px radius, subtle focus ring (uses `!` to beat any global input styles)
const FIELD_BASE =
  "w-full h-11 rounded-lg border bg-white text-[13px] text-[#172B4D] transition-colors " +
  "placeholder:text-[#9CA3AF] focus:!outline-none focus:!ring-0";

const fieldStateClasses = (hasError?: boolean) =>
  hasError
    ? "border-[#DC2626] focus:!border-[#DC2626]"
    : "border-[#D1D5DB] hover:border-[#9CA3AF] focus:!border-[#D1D5DB]";

const validationSchema = yup.object({
  firstName: yup.string().required("First Name is required"),
  lastName: yup.string().required("Last Name is required"),
  email: yup
    .string()
    .required("Email is required")
    .test(
      "is-valid-email",
      "Please enter a valid email address",
      (value) => !value || isValidEmail(value)
    ),
  website: yup
    .string()
    .optional()
    .default("")
    .test(
      "valid-website",
      "Please enter a valid website ending in .com, .in, or .org",
      (value) => {
        if (!value) return true;
        return isValidWebsite(value);
      }
    ),
  companyName: yup.string().required("Company Name is required"),
  phoneNumber: yup.string().required("Phone Number is required"),
  numberOfEmployees: yup.string().optional().default(""),
  plan: yup.string().optional().default(""),
  productName: yup.string().required("Product Interest is required"),
  timeline: yup.string().optional().default(""),
});

type FormData = yup.InferType<typeof validationSchema>;

/* ------------------------------------------------------------------ */
/*  Reusable presentational pieces                                     */
/* ------------------------------------------------------------------ */

// Brand mark + wordmark
const Logo = () => (
  <div className="flex items-center gap-3">
    <svg viewBox="0 0 64 64" className="h-10 w-10 flex-shrink-0" aria-hidden="true" focusable="false">
      <path
        d="M10 8c0-3.3 2.7-6 6-6h8v44h18c3.3 0 6 2.7 6 6v6H16c-3.3 0-6-2.7-6-6V8z"
        fill={COLORS.primary}
      />
      <rect x="28" y="12" width="14" height="14" rx="4" fill={COLORS.primary} />
      <rect x="44" y="12" width="14" height="14" rx="4" fill={COLORS.primary} />
      <rect x="28" y="28" width="14" height="14" rx="4" fill={COLORS.primary} />
      <path d="M44 28h8l6 6v8h-8a6 6 0 0 1-6-6v-8z" fill={COLORS.primary} />
    </svg>
    <div>
      <div className="text-[26px] font-bold leading-none text-[#172B4D]">Liriworks</div>
      <div className="text-[12px] text-[#6B7280]">All-in-one platform</div>
    </div>
  </div>
);

// A single marketing feature row
const FeatureItem = ({
  icon: Icon,
  title,
  description,
}: {
  icon: React.ComponentType<{ size?: string | number; className?: string }>;
  title: string;
  description: string;
}) => (
  <div className="flex items-start gap-3">
    <div className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#EEF4FF]">
      <Icon size={16} className="text-[#2F63D9]" />
    </div>
    <div>
      <p className="text-[14px] font-semibold leading-tight text-[#172B4D]">{title}</p>
      <p className="mt-0.5 text-[12px] leading-snug text-[#6B7280]">{description}</p>
    </div>
  </div>
);

type InputProps = {
  name: keyof FormData;
  label: string;
  type?: string;
  placeholder?: string;
  icon?: React.ComponentType<{ size?: string | number }>;
  required?: boolean;
  autoCapitalizeFirstChar?: boolean;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  onInput?: React.FormEventHandler<HTMLInputElement>;
};

// Text input wired to react-hook-form (label + icon + error, layout-stable)
const Input = ({
  name,
  label,
  type = "text",
  placeholder,
  icon: Icon,
  required = false,
  autoCapitalizeFirstChar = false,
  onKeyDown,
  onInput,
}: InputProps) => {
  const {
    register,
    formState: { errors },
  } = useFormContext<FormData>();

  const fieldError = errors[name];
  const error = typeof fieldError?.message === "string" ? fieldError.message : undefined;

  return (
    <div className="w-full">
      <label htmlFor={name} className="mb-1 block text-[12px] font-medium text-[#374151]">
        {label} {required ? <span className="text-[#DC2626]">*</span> : null}
      </label>

      <div className="relative">
        {Icon && (
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]">
            <Icon size={16} />
          </span>
        )}

        <input
          id={name}
          aria-required={required || undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${name}-error` : undefined}
          {...register(name, {
            onChange: (event) => {
              if (!autoCapitalizeFirstChar) return;

              const value = event?.target?.value;
              if (typeof value !== "string" || value.length === 0) return;

              const capitalized = value.charAt(0).toUpperCase() + value.slice(1);
              if (capitalized !== value) {
                const { selectionStart, selectionEnd } = event.target;
                event.target.value = capitalized;

                if (selectionStart !== null && selectionEnd !== null) {
                  event.target.setSelectionRange(selectionStart, selectionEnd);
                }
              }
            },
          })}
          onKeyDown={onKeyDown}
          onInput={onInput}
          type={type}
          placeholder={placeholder}
          className={`${FIELD_BASE} ${Icon ? "pl-10 pr-3" : "px-3"} ${fieldStateClasses(!!error)}`}
        />
      </div>

      <p
        id={`${name}-error`}
        className={`mt-1 text-[11px] text-[#DC2626] ${error ? "visible" : "invisible"}`}
      >
        {error || "\u00A0"}
      </p>
    </div>
  );
};

// Native select (keyboard accessible) with a custom chevron for a consistent look
const SelectField = ({
  name,
  label,
  options,
  required = false,
  placeholder,
}: {
  name: keyof FormData;
  label: string;
  options: Array<{ value: string; label: string }>;
  required?: boolean;
  placeholder?: string;
}) => {
  const {
    register,
    watch,
    formState: { errors },
  } = useFormContext<FormData>();

  const error = errors[name]?.message as string;
  const selectedValue = watch(name);

  return (
    <div className="w-full">
      <label htmlFor={name} className="mb-1 block text-[12px] font-medium text-[#374151]">
        {label} {required ? <span className="text-[#DC2626]">*</span> : null}
      </label>

      <div className="relative">
        <select
          id={name}
          aria-required={required || undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${name}-error` : undefined}
          {...register(name)}
          className={`${FIELD_BASE} appearance-none px-3 pr-9 ${fieldStateClasses(!!error)}`}
          style={{
            // Force-remove the browser's native dropdown arrow (global CSS can re-enable it),
            // so only the custom ChevronDown below is shown
            WebkitAppearance: "none",
            MozAppearance: "none",
            appearance: "none",
            backgroundImage: "none",
            ...(!selectedValue && placeholder ? { color: COLORS.placeholder } : {}),
          }}
        >
          {placeholder ? (
            <option value="" disabled hidden>
              {placeholder}
            </option>
          ) : null}
          {options.map((option) => (
            <option key={option.value} value={option.value} style={{ color: COLORS.heading }}>
              {option.label}
            </option>
          ))}
        </select>
        <ChevronDown
          size={16}
          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]"
        />
      </div>

      <p
        id={`${name}-error`}
        className={`mt-1 text-[11px] text-[#DC2626] ${error ? "visible" : "invisible"}`}
      >
        {error || "\u00A0"}
      </p>
    </div>
  );
};

// Marketing feature content (matches the reference)
const features = [
  { icon: Users, title: "Centralized Operations", description: "Keep all employee information in one secure place." },
  { icon: CheckSquare, title: "Boost Productivity", description: "Track tasks, projects and timesheets in real time." },
  { icon: Clock, title: "Simplify Workflows", description: "Automate leave, reimbursement and approvals." },
  { icon: Calendar, title: "Smart Scheduling", description: "Plan shifts and manage employee schedules." },
  { icon: BarChart3, title: "Data-Driven Decisions", description: "Get insights and reports to make better decisions." },
];

export default function Registration() {
  const { addToast } = useToaster();
  const navigate = useNavigate();
  const [, setShowToast] = useState(false);
  const [, setToastMessage] = useState("");
  const [countryCode, setCountryCode] = useState("+91");

  const countryCodeRef = useRef(countryCode);

  const dynamicResolver: Resolver<FormData> = useCallback(
    async (values, context, options) => {
      const schemaWithPhoneValidation = validationSchema.shape({
        phoneNumber: yup
          .string()
          .required("Phone Number is required")
          .test("phone-by-country", function (value) {
            if (!value) return true;

            const currentCode = countryCodeRef.current;
            // 1. Strip non-digits from user input
            const digits = String(value).replace(/\D/g, "");
            if (!digits) return this.createError({ message: "Phone Number is required" });

            // 2. Get the exact 2-letter ISO code from your dropdown ONLY for validation (e.g., 'IN', 'US', 'GB')
            const selectedOption = countryCodeOptions.find((opt) => opt.value === currentCode);
            const isoCode = selectedOption
              ? (selectedOption.label.split(" ")[0] as CountryCode)
              : undefined;

            // 3. FORMAT & STRICT VALIDATION
            try {
              // Parse the number with the selected ISO code
              const phoneNumber = parsePhoneNumberWithError(digits, isoCode);

              // .isValid() checks exact telecom rules for the region (e.g. 10 digits for UK, 9 for UAE/SA)
              if (!phoneNumber.isValid()) {
                return this.createError({ message: "Invalid phone number format for this region" });
              }

              return true;
            } catch (error) {
              if (error instanceof ParseError) {
                switch (error.message) {
                  case "INVALID_COUNTRY":
                    return this.createError({ message: "Invalid country code." });
                  case "NOT_A_NUMBER":
                    return this.createError({ message: "Phone number contains invalid characters." });
                  case "TOO_SHORT":
                    return this.createError({ message: "Phone number is too short." });
                  case "TOO_LONG":
                    return this.createError({ message: "Phone number is too long." });
                }
              }
              return this.createError({ message: "Invalid phone number." });
            }
          }),
      });

      const resolver = yupResolver(schemaWithPhoneValidation);
      return resolver(values, context, options);
    },
    []
  );

  const methods = useForm<FormData>({
    resolver: dynamicResolver,
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      website: "",
      companyName: "",
      phoneNumber: "",
      numberOfEmployees: "",
      plan: "",
      productName: "Liriworks",
      timeline: "",
    },
  });

  const {
    formState: { isSubmitting, errors },
    trigger,
    getValues,
  } = methods;

  useEffect(() => {
    countryCodeRef.current = countryCode;
    if (getValues("phoneNumber")) {
      void trigger("phoneNumber");
    }
  }, [countryCode, getValues, trigger]);

  const companySizeOptions = [
    { value: "1-10", label: "1-10" },
    { value: "11-50", label: "11-50" },
    { value: "51-200", label: "51-200" },
    { value: "201-500", label: "201-500" },
    { value: "500+", label: "500+" },
  ];

  const planOptions = [
    { label: "Trial", value: "TRIAL" },
    { label: "Basic", value: "BASIC" },
    { label: "Premium", value: "PREMIUM" },
    { label: "Enterprise", value: "ENTERPRISE" },
  ];



  const onSubmit = async (data: FormData) => {
    const payload = {
      ...data,
      phoneNumber: `${countryCode}${data.phoneNumber}`.trim(),
      email: data.email.toLowerCase().trim(),
    };

    try {
      // 1. Wait for successful API call
      const registrationUrl = `${appConfig.getRegistrationApiUrl()}registration`;
      const response = await axios.post(registrationUrl, payload);

      // 2. Extract plan directly from the payload
      const planValue = payload?.plan;
      const selectedPlanName = (typeof planValue === 'string' && planValue.trim() !== '')
        ? planValue
        : "Trial";

      // 3. Extract registrationId from response and convert the number to a string
      const responseData = response?.data;

      const actualData = responseData?.data ? responseData.data : responseData;

      const registrationId = actualData?.id ? String(actualData.id) : "";

      // 4. Check if plan is NOT Trial (Comparing lowercase)
      if (selectedPlanName.toLowerCase() !== "trial") {
        addToast("Registration successful. Please select a plan.", "success");
        setToastMessage("Registration successful. Please select a plan.");
        setShowToast(true);
        setTimeout(() => setShowToast(false), 2500);

        // SECURE STATE: Save stringified ID to sessionStorage to survive refreshes
        if (registrationId) {
          sessionStorage.setItem("registrationId", registrationId);
        }
        sessionStorage.setItem("initialPlanName", selectedPlanName);

        // Redirect to plan-selection with the correct stringified registration ID
        navigate('/plan-selection', {
          state: {
            initialPlanName: selectedPlanName,
            registrationId: registrationId
          }
        });
      } else {
        // Trial Flow / Existing Flow
        addToast("Registration submitted successfully", "success");
        setToastMessage("Registration submitted successfully");
        setShowToast(true);
        setTimeout(() => setShowToast(false), 2500);

        methods.reset({
          firstName: "",
          lastName: "",
          email: "",
          website: "",
          companyName: "",
          phoneNumber: "",
          numberOfEmployees: "",
          plan: "",
          productName: "Liriworks",
          timeline: "",
        });
        setCountryCode("+91");
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error("Registration Error", error);
      addToast(error.response?.data?.message || "Registration failed", "error");
      setToastMessage(error.response?.data?.message || "Registration failed");
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2500);
    }
  };

  // Extract the phone error once (typed as string) and reuse it below,
  // mirroring the pattern used inside the Input/SelectField components.
  const phoneError =
    typeof errors.phoneNumber?.message === "string"
      ? errors.phoneNumber.message
      : undefined;

  const formatWebsiteInput = (e: React.FormEvent<HTMLInputElement>) => {
    const input = e.currentTarget;

    // Remove spaces and force lowercase automatically
    const formattedValue = input.value.replace(/\s+/g, "").toLowerCase();

    if (input.value !== formattedValue) {
      const cursor = input.selectionStart ?? formattedValue.length;
      input.value = formattedValue;

      // Restore cursor position seamlessly
      requestAnimationFrame(() => {
        input.setSelectionRange(cursor, cursor);
      });
    }
  };

  return (
    // Scroll container fixed to the viewport (html/body have `overflow: hidden` globally, so the
    // page itself can't scroll). The inner wrapper centres the content and scrolls on short screens.
    <div className="h-dvh w-full overflow-y-auto bg-[#F3F6FC]">
      <div className="mx-auto flex min-h-full max-w-[1320px] items-center justify-center p-4 sm:p-6 lg:p-8">
        {/* Two-column marketing + form layout — two columns from the md breakpoint (768px) up,
            stacked and centred below that so it stays balanced on tablets/laptops */}
        <div className="grid w-full grid-cols-1 lg:grid-cols-2 items-center gap-4 md:grid-cols-[48fr_52fr] md:gap-10 lg:gap-12">

          {/* ── LEFT MARKETING SECTION ── */}
          <section className="mx-auto flex w-full max-w-[460px] flex-col justify-center md:mx-0 md:max-w-none">
            <Logo />

            <h1 className="mt-6 text-[28px] font-bold leading-[1.1] text-[#172B4D] sm:text-[32px]">
              Get Started
              <br />
              with Liriworks
            </h1>

            <p className="mt-3 max-w-[440px] text-[14px] leading-relaxed text-[#6B7280]">
              Create your account and start managing your team smarter.
            </p>

            {/* Feature list */}
            <div className="mt-6 flex flex-col gap-4">
              {features.map((f) => (
                <FeatureItem key={f.title} icon={f.icon} title={f.title} description={f.description} />
              ))}
            </div>

            {/* Illustration container — ~390×215, preserves aspect ratio, left-aligned */}
            <div className="mt-8 flex h-[180px] w-full max-w-[390px] items-center justify-center overflow-hidden rounded-lg border border-[#D1D5DB] bg-[#EEF4FF] sm:h-[215px]">
              <img
                src={registrationImage}
                alt="Team collaborating on the Liriworks platform"
                className="h-full w-full object-contain"
              />
            </div>
          </section>

          {/* ── RIGHT REGISTRATION CARD ── */}
          <section className="flex justify-center">
            <div className="flex w-full max-w-[760px] flex-col rounded-2xl border border-[#E5E9F0] bg-white p-6 shadow-[0_8px_24px_rgba(23,43,77,0.08)] sm:p-8 lg:p-9">
              <h2 className="text-[26px] font-bold leading-tight text-[#172B4D]">Sign Up</h2>
              <p className="mt-1 text-[12px] text-[#6B7280]">Create your account to get started</p>

              <FormProvider {...methods}>
                <form
                  id="registration-form"
                  noValidate
                  onSubmit={methods.handleSubmit(onSubmit)}
                  className="mt-5 flex flex-1 flex-col"
                >
                  <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
                    {/* Row 1 — First / Last name */}
                    <Input name="firstName" onInput={allowOnlyAlphabets} label="First Name" placeholder="John" icon={User} required autoCapitalizeFirstChar />
                    <Input name="lastName" onInput={allowOnlyAlphabets} label="Last Name" placeholder="Doe" icon={User} required autoCapitalizeFirstChar />

                    {/* Row 2 — Work email (full width) */}
                    <div className="sm:col-span-2">
                      <Input
                        name="email"
                        label="Work Email"
                        placeholder="john.doe@company.com"
                        icon={Mail}
                        required
                        onKeyDown={preventSpaceInput}
                        onInput={removeSpacesFromInput}
                      />
                    </div>

                    {/* Row 3 — Website (full width) */}
                    <div className="sm:col-span-2">
                      <Input
                        name="website"
                        label="Website"
                        placeholder="https://www.yourcompany.com"
                        icon={Globe}
                        onInput={formatWebsiteInput}
                      />
                    </div>

                    {/* Row 4 — Company name / Phone number */}
                    <Input name="companyName" label="Company Name" placeholder="Your Company" icon={Building2} required autoCapitalizeFirstChar />

                    {/* PhoneInput — country code visually separated from the number */}
                    <div className="w-full">
                      <label htmlFor="phoneNumber" className="mb-1 block text-[12px] font-medium text-[#374151]">
                        Phone Number <span className="text-[#DC2626]">*</span>
                      </label>

                      <div className="flex h-11 w-full items-center rounded-lg shadow-sm">
                        {/* Country code */}
                        <div className="relative h-full">
                          <select
                            value={countryCode}
                            onChange={(e) => setCountryCode(e.target.value)}
                            className="relative z-0 h-full appearance-none rounded-l-lg border border-r-0 !border-[#D1D5DB] bg-white pl-3 pr-7 text-[13px] text-[#172B4D] focus:!outline-none focus:!ring-0"
                            aria-label="Country calling code"
                          >
                            {countryCodeOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Phone number */}
                        <div className="relative h-full flex-1">
                          {/* <span className="pointer-events-none absolute left-3 top-1/2 z-20 -translate-y-1/2 text-[#9CA3AF]">
      <Phone size={16} />
    </span> */}
                          <input
                            id="phoneNumber"
                            aria-required
                            aria-invalid={phoneError ? true : undefined}
                            aria-describedby={phoneError ? "phoneNumber-error" : undefined}
                            {...methods.register("phoneNumber")}
                            type="text"
                            inputMode="numeric"
                            placeholder="(555) 000-0000"
                            onKeyDown={(e) => {
                              if (!/^\d$/.test(e.key) && !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            onPaste={(e) => {
                              const pasted = e.clipboardData.getData("text");
                              if (!/^\d+$/.test(pasted)) {
                                e.preventDefault();
                              }
                            }}
                            className="relative z-0 h-full w-full rounded-r-lg border !border-[#D1D5DB] bg-white pl-9 pr-3 text-[13px] text-[#172B4D] placeholder:text-[#9CA3AF] focus:!outline-none focus:!ring-0"
                          />
                        </div>
                      </div>

                      <p
                        id="phoneNumber-error"
                        className={`mt-1 text-[11px] text-[#DC2626] ${phoneError ? "visible" : "invisible"}`}
                      >
                        {phoneError || "\u00A0"}
                      </p>
                    </div>

                    {/* Row 5 — Company size / Plan */}
                    <SelectField name="numberOfEmployees" label="Company Size" placeholder="Select size" options={companySizeOptions} />
                    <SelectField name="plan" label="Plan" required placeholder="Select plan" options={planOptions} />
                  </div>

                  {/* Primary CTA + footer, pinned to the bottom of the card */}
                  <div className="mt-auto pt-2">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg !bg-[#2F63D9] text-[13px] font-semibold !text-white shadow-sm transition-colors hover:!bg-[#2551B8] active:!bg-[#1F47A0] disabled:cursor-not-allowed disabled:opacity-60 focus:!outline-none focus:!ring-2 focus:!ring-[#2F63D9]/40 focus:!ring-offset-2"
                    >
                      {isSubmitting && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
                      {isSubmitting ? "Signing Up..." : "Sign Up"}
                    </button>

                    <p className="mt-3 text-center text-[12px] text-[#6B7280]">
                      Already have an account?{" "}
                      <a href="/login" className="font-semibold text-[#2F63D9] hover:underline">
                        Sign In
                      </a>
                    </p>
                  </div>
                </form>
              </FormProvider>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
